<script setup lang="ts">
import type { CropRect } from '@@/types/api/common';
import type {
  CoverArtController,
  CoverArtPresignFn,
} from '~/composables/use-cover-art';
import { uploadToS3 } from '~/composables/use-s3-upload';
import { revokeBlobUrl, toCoverArtPresignBody } from '~/utils/cover-art-state';

const props = withDefaults(
  defineProps<{
    /** State host — the Pinia event-form store, or `useCoverArt()`. */
    controller: CoverArtController;
    /**
     * Transport. Owns everything host-specific: which endpoint to hit, which
     * identifier to attach, and (via the returned `commit` thunk) any
     * write-back that must happen only after the S3 PUT succeeds.
     */
    presign: CoverArtPresignFn;
    disabled?: boolean;
    /** Lowercase noun for the copy strings ("event", "idea", …). */
    subjectNoun?: string;
    /**
     * Hide this component's own alert for UPLOAD failures (presign transport /
     * S3 PUT). `uploadCoverArt()` still flips `runtimeState` to 'failed' and
     * still RETURNS the message — every host already surfaces that itself, so
     * the built-in alert is a duplicate. Hosts that render the returned message
     * somewhere better (e.g. next to their own submit button) opt out here.
     *
     * File-SELECTION errors are never suppressed: nothing outside this
     * component ever sees those, so its alert is their only surface.
     *
     * Defaults to false to keep the native event create/edit flows unchanged.
     */
    suppressUploadErrorAlert?: boolean;
  }>(),
  { disabled: false, subjectNoun: 'event', suppressUploadErrorAlert: false },
);

/**
 * Read the host's state through a computed so that a whole-object replacement
 * (the store's `clearCoverArt` / `$reset` reassign `coverArt.value`) still
 * propagates to every template binding and `deep` watcher below.
 */
const art = computed(() => props.controller.coverArt);

// -----------------------------------------------------------------------------
// Subject-specific copy
// -----------------------------------------------------------------------------

const subjectLabel = computed(
  () => props.subjectNoun.charAt(0).toUpperCase() + props.subjectNoun.slice(1),
);
const aspect16Surfaces = computed(
  () => `${subjectLabel.value} pages and cards`,
);
const aspect16Hint = computed(
  () =>
    `Used on the ${props.subjectNoun} detail page hero and every browse/dashboard card.`,
);
const aspect11Hint = computed(
  () =>
    `Used when your ${props.subjectNoun} is shared on Facebook, LinkedIn, X, Discord, iMessage, WhatsApp, or Slack.`,
);

// -----------------------------------------------------------------------------
// Local state
// -----------------------------------------------------------------------------

const uploadProgress = ref(0);
const errorMessage = ref<string | null>(null);
const isProbing = ref(false);

type CoverEditMode = 'focal' | 'advanced';
const cropMode = ref<CoverEditMode>('focal');

const fileInput = ref<HTMLInputElement | null>(null);
const file = ref<File | null>(null);
const natural = ref<{ w: number; h: number }>({ w: 0, h: 0 });

// The rendered <img> for the focal-point overlay.
const focalImgEl = ref<HTMLImageElement | null>(null);
const focalOverlayEl = ref<HTMLDivElement | null>(null);
const focalDisplay = ref<{ w: number; h: number }>({ w: 0, h: 0 });

// Preview canvases.
const heroCanvasEl = ref<HTMLCanvasElement | null>(null);
const cardCanvasEl = ref<HTMLCanvasElement | null>(null);
const ogCanvasEl = ref<HTMLCanvasElement | null>(null);

const MAX_BYTES = 25 * 1024 * 1024; // 25MB
const ALLOWED_MIMES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];
const ACCEPT_ATTR = ALLOWED_MIMES.join(',');

// -----------------------------------------------------------------------------
// File pick + probing
// -----------------------------------------------------------------------------

function openFilePicker() {
  fileInput.value?.click();
}

async function handleFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  const picked = input.files?.[0];
  if (!picked) return;

  errorMessage.value = null;

  if (picked.size > MAX_BYTES) {
    errorMessage.value = 'Image must be smaller than 25MB';
    input.value = '';
    return;
  }

  if (!ALLOWED_MIMES.includes(picked.type)) {
    errorMessage.value = 'File must be a JPEG, PNG, WebP, or HEIC image';
    input.value = '';
    return;
  }

  isProbing.value = true;

  try {
    const bitmap = await createImageBitmap(picked);
    natural.value = { w: bitmap.width, h: bitmap.height };
    bitmap.close?.();
  } catch {
    errorMessage.value =
      "Your browser can't preview HEIC images. Convert to JPEG in Photos, or open this page in Safari.";
    isProbing.value = false;
    input.value = '';
    return;
  }

  // Clear any previous blob URL first.
  revokeBlobUrl(art.value.originalUrl);

  file.value = picked;
  art.value.originalUrl = URL.createObjectURL(picked);
  art.value.mediaAssetId = null;
  art.value.runtimeState = 'idle';
  // Record dimensions before recentring so setFocalPoint's crop recompute
  // sees the correct image aspect. Then recentre focal + recompute both
  // auto-crops.
  props.controller.setNaturalDimensions(natural.value);
  props.controller.setFocalPoint({ x: 0.5, y: 0.5 });

  isProbing.value = false;
  input.value = '';
}

// -----------------------------------------------------------------------------
// Focal-point drag
// -----------------------------------------------------------------------------

function onFocalImgLoad(e: Event) {
  const img = e.target as HTMLImageElement;
  focalDisplay.value = {
    w: img.clientWidth,
    h: img.clientHeight,
  };
  // In edit mode we may not have oriented dimensions yet (no createImageBitmap
  // was run because the user didn't pick a file). Fill from the loaded <img>
  // and propagate to the store so any non-overridden auto-crops recompute
  // with the correct image aspect ratio. We don't touch the focal point here
  // — it's already been hydrated from the API in edit mode.
  if (natural.value.w === 0 || natural.value.h === 0) {
    natural.value = { w: img.naturalWidth, h: img.naturalHeight };
    props.controller.setNaturalDimensions(natural.value);
  }
  drawPreviews();
}

let dragActive = false;
let dragPointerId: number | null = null;
let rafPending = false;
let pendingFocal: { x: number; y: number } | null = null;

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0.5;
  return Math.max(0, Math.min(1, v));
}

function flushFocal() {
  rafPending = false;
  if (pendingFocal) {
    props.controller.setFocalPoint(pendingFocal);
    pendingFocal = null;
  }
}

function scheduleFocal(focal: { x: number; y: number }) {
  pendingFocal = focal;
  if (rafPending) return;
  rafPending = true;
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(flushFocal);
  } else {
    flushFocal();
  }
}

function focalFromEvent(e: PointerEvent): { x: number; y: number } | null {
  const img = focalImgEl.value;
  if (!img) return null;
  const rect = img.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  return {
    x: clamp01((e.clientX - rect.left) / rect.width),
    y: clamp01((e.clientY - rect.top) / rect.height),
  };
}

function onOverlayPointerDown(e: PointerEvent) {
  if (props.disabled) return;
  const overlay = focalOverlayEl.value;
  if (!overlay) return;
  try {
    overlay.setPointerCapture(e.pointerId);
  } catch {
    // Some browsers throw when the pointer isn't yet captured; ignore.
  }
  dragActive = true;
  dragPointerId = e.pointerId;
  const focal = focalFromEvent(e);
  if (focal) scheduleFocal(focal);
  e.preventDefault();
}

function onOverlayPointerMove(e: PointerEvent) {
  if (!dragActive || e.pointerId !== dragPointerId) return;
  const focal = focalFromEvent(e);
  if (focal) scheduleFocal(focal);
}

function onOverlayPointerUp(e: PointerEvent) {
  if (e.pointerId !== dragPointerId) return;
  const overlay = focalOverlayEl.value;
  try {
    overlay?.releasePointerCapture(e.pointerId);
  } catch {
    // ignore
  }
  dragActive = false;
  dragPointerId = null;
}

function onOverlayKeydown(e: KeyboardEvent) {
  if (props.disabled) return;
  const step = 0.01;
  const cur = art.value.focalPoint;
  let dx = 0;
  let dy = 0;
  switch (e.key) {
    case 'ArrowLeft':
      dx = -step;
      break;
    case 'ArrowRight':
      dx = step;
      break;
    case 'ArrowUp':
      dy = -step;
      break;
    case 'ArrowDown':
      dy = step;
      break;
    default:
      return;
  }
  e.preventDefault();
  props.controller.setFocalPoint({
    x: clamp01(cur.x + dx),
    y: clamp01(cur.y + dy),
  });
}

const focalMarkerStyle = computed(() => {
  const { x, y } = art.value.focalPoint;
  const px = x * focalDisplay.value.w;
  const py = y * focalDisplay.value.h;
  return {
    left: `${px}px`,
    top: `${py}px`,
  };
});

const focalOverlayStyle = computed(() => ({
  width: `${focalDisplay.value.w}px`,
  height: `${focalDisplay.value.h}px`,
}));

// -----------------------------------------------------------------------------
// Preview canvas rendering
// -----------------------------------------------------------------------------

function drawCrop(
  canvas: HTMLCanvasElement | null,
  img: HTMLImageElement | null,
  crop: CropRect,
) {
  if (!canvas || !img) return;
  if (!img.complete || img.naturalWidth === 0) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const dpr =
    typeof window !== 'undefined' && window.devicePixelRatio
      ? window.devicePixelRatio
      : 1;
  const cssW = canvas.clientWidth;
  const cssH = canvas.clientHeight;
  if (cssW === 0 || cssH === 0) return;
  const pixelW = Math.round(cssW * dpr);
  const pixelH = Math.round(cssH * dpr);
  if (canvas.width !== pixelW) canvas.width = pixelW;
  if (canvas.height !== pixelH) canvas.height = pixelH;

  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  const sx = crop.x * nw;
  const sy = crop.y * nh;
  const sw = crop.w * nw;
  const sh = crop.h * nh;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
}

function drawPreviews() {
  const img = focalImgEl.value;
  if (!img) return;
  drawCrop(heroCanvasEl.value, img, art.value.crops['16_9']);
  drawCrop(cardCanvasEl.value, img, art.value.crops['16_9']);
  drawCrop(ogCanvasEl.value, img, art.value.crops['1_1']);
}

watch(
  () => art.value.crops,
  () => {
    // Wait a tick so any advanced-panel canvas mount can settle first.
    nextTick(drawPreviews);
  },
  { deep: true },
);

// -----------------------------------------------------------------------------
// Cropper pixel <-> fractional conversion
// -----------------------------------------------------------------------------

// Cropper's stencil coordinates are in image pixel space (natural, oriented).
// Convert a fractional CropRect into cropper `{ left, top, width, height }`.
function cropRectToPixels(
  rect: CropRect,
  nat: { w: number; h: number },
): { left: number; top: number; width: number; height: number } {
  return {
    left: Math.round(rect.x * nat.w),
    top: Math.round(rect.y * nat.h),
    width: Math.round(rect.w * nat.w),
    height: Math.round(rect.h * nat.h),
  };
}

// Convert cropper coordinates back into fractional CropRect. `nat` is the
// image size the cropper reported in its `image` payload (oriented pixels).
function pixelsToCropRect(
  coords: { left: number; top: number; width: number; height: number },
  nat: { width: number; height: number },
): CropRect {
  if (nat.width === 0 || nat.height === 0) {
    return { x: 0, y: 0, w: 1, h: 1 };
  }
  const x = clamp01(coords.left / nat.width);
  const y = clamp01(coords.top / nat.height);
  const w = clamp01(coords.width / nat.width);
  const h = clamp01(coords.height / nat.height);
  return { x, y, w, h };
}

// -----------------------------------------------------------------------------
// Cropper default-position/size closures (fresh on each mount)
// -----------------------------------------------------------------------------

function defaultPosition16_9() {
  const px = cropRectToPixels(art.value.crops['16_9'], natural.value);
  return { left: px.left, top: px.top };
}
function defaultSize16_9() {
  const px = cropRectToPixels(art.value.crops['16_9'], natural.value);
  return { width: px.width, height: px.height };
}
function defaultPosition1_1() {
  const px = cropRectToPixels(art.value.crops['1_1'], natural.value);
  return { left: px.left, top: px.top };
}
function defaultSize1_1() {
  const px = cropRectToPixels(art.value.crops['1_1'], natural.value);
  return { width: px.width, height: px.height };
}

interface CropperChange {
  coordinates: { left: number; top: number; width: number; height: number };
  image: { width: number; height: number };
}

// vue-advanced-cropper's `@change` fires on mount (and on image reload) with
// the initial default-position/default-size values. Blindly treating every
// emission as user-initiated would flip `overrides[aspect]` to true the moment
// the Advanced section renders, which then locks that aspect out of focal-point
// propagation. Compare the emitted rect against the store's current crop and
// only flip the override when it actually differs.
function cropsEqual(a: CropRect, b: CropRect, eps = 0.001): boolean {
  return (
    Math.abs(a.x - b.x) < eps &&
    Math.abs(a.y - b.y) < eps &&
    Math.abs(a.w - b.w) < eps &&
    Math.abs(a.h - b.h) < eps
  );
}

function onCropperChange16_9(change: CropperChange) {
  const rect = pixelsToCropRect(change.coordinates, change.image);
  if (cropsEqual(rect, art.value.crops['16_9'])) return;
  props.controller.setOverrideCrop('16_9', rect);
}

function onCropperChange1_1(change: CropperChange) {
  const rect = pixelsToCropRect(change.coordinates, change.image);
  if (cropsEqual(rect, art.value.crops['1_1'])) return;
  props.controller.setOverrideCrop('1_1', rect);
}

// vue-advanced-cropper reads default-position/default-size only at mount (or
// when `src` changes). After that, stencil position lives in the cropper's
// internal state and won't respond to prop updates. To visually reset the
// stencil after "Reset to focal-point crop", we bump a reactive :key so Vue
// unmounts + remounts the cropper, forcing a re-read of the (now-reset)
// default-position/default-size closures. The mount-fires-change guard
// (cropsEqual) absorbs the synthetic @change from the remount.
const cropperKey16_9 = ref(0);
const cropperKey1_1 = ref(0);

function resetOverride16_9() {
  props.controller.resetAspectOverride('16_9');
  cropperKey16_9.value++;
}
function resetOverride1_1() {
  props.controller.resetAspectOverride('1_1');
  cropperKey1_1.value++;
}

// -----------------------------------------------------------------------------
// Remove
// -----------------------------------------------------------------------------

function removeImage() {
  file.value = null;
  uploadProgress.value = 0;
  errorMessage.value = null;
  cropMode.value = 'focal';
  props.controller.clearCoverArt();
}

// -----------------------------------------------------------------------------
// Upload (parent-triggered)
// -----------------------------------------------------------------------------

/**
 * Record an upload failure: mark the slice failed, surface the message locally
 * unless the host opted out, and hand the message back either way.
 */
function failUpload(message: string): { success: false; error: string } {
  art.value.runtimeState = 'failed';
  if (!props.suppressUploadErrorAlert) errorMessage.value = message;
  return { success: false, error: message };
}

async function uploadCoverArt(): Promise<{
  success: boolean;
  error?: string;
}> {
  // Nothing to upload: no picked file OR no original URL loaded.
  if (!file.value || !art.value.originalUrl) {
    return { success: true };
  }
  // Already uploaded in this session.
  if (art.value.mediaAssetId) {
    return { success: true };
  }

  // Guard against sending zeros for natural dimensions. If we get here with an
  // unpopulated `natural`, something's wrong: the image loaded (originalUrl is
  // set) and focal-point drag worked, but setNaturalDimensions was never
  // called — better to surface a clear error than to fail the backend's
  // pixel-space aspect validation with a cryptic 422.
  if (art.value.natural.w <= 0 || art.value.natural.h <= 0) {
    art.value.runtimeState = 'failed';
    errorMessage.value =
      'Could not read the image dimensions. Please pick the file again.';
    return { success: false, error: errorMessage.value };
  }

  errorMessage.value = null;
  uploadProgress.value = 0;
  art.value.runtimeState = 'uploading';

  try {
    const response = await props.presign(
      toCoverArtPresignBody(art.value, file.value),
    );

    const result = await uploadToS3(
      response.presigned_url,
      file.value,
      (pct) => {
        uploadProgress.value = pct;
      },
    );

    if (!result.success) {
      return failUpload(result.error ?? 'Upload failed');
    }

    art.value.mediaAssetId = response.media_asset_id;
    // Ordering is load-bearing: the host's write-back runs only once the S3
    // PUT has actually succeeded, never on presign success alone.
    response.commit?.();
    art.value.runtimeState = 'processing';
    return { success: true };
  } catch (e: unknown) {
    return failUpload(
      e && typeof e === 'object' && 'message' in e
        ? String((e as { message: unknown }).message)
        : 'Failed to get upload URL. Please try again.',
    );
  }
}

defineExpose({ uploadCoverArt });

// -----------------------------------------------------------------------------
// Cleanup
// -----------------------------------------------------------------------------

onBeforeUnmount(() => {
  if (rafPending) {
    // Flush any pending focal update on unmount.
    flushFocal();
  }
});
</script>

<template>
  <div class="cover-image-upload">
    <input
      ref="fileInput"
      type="file"
      :accept="ACCEPT_ATTR"
      class="d-none"
      :disabled="props.disabled"
      @change="handleFileChange"
    />

    <!-- Empty state -->
    <div v-if="!art.originalUrl">
      <v-btn
        variant="outlined"
        prepend-icon="mdi-image-plus"
        :loading="isProbing"
        :disabled="props.disabled"
        @click="openFilePicker"
      >
        Upload cover image
      </v-btn>
      <div class="text-caption text-medium-emphasis mt-1">
        JPEG, PNG, WebP, or HEIC. Max 25 MB.
      </div>
    </div>

    <!-- Loaded state -->
    <div v-else class="cover-editor">
      <!-- Focal-point mode: original image + focal marker + live previews -->
      <div v-if="cropMode === 'focal'">
        <v-row dense>
          <!-- Focal-point original -->
          <v-col cols="12" md="6">
            <div class="cover-panel">
              <div class="d-flex align-center mb-2">
                <div class="text-subtitle-2">Focal point</div>
                <v-spacer />
                <v-btn
                  v-if="!props.disabled"
                  icon="mdi-close"
                  size="x-small"
                  variant="text"
                  color="error"
                  aria-label="Remove cover image"
                  @click="removeImage"
                />
              </div>

              <div class="focal-image-wrap">
                <img
                  ref="focalImgEl"
                  :src="art.originalUrl"
                  alt="Cover image original"
                  class="focal-image"
                  draggable="false"
                  @load="onFocalImgLoad"
                />
                <div
                  v-if="focalDisplay.w > 0"
                  ref="focalOverlayEl"
                  class="focal-overlay"
                  :style="focalOverlayStyle"
                  role="slider"
                  tabindex="0"
                  aria-label="Focal point"
                  :aria-valuenow="Math.round(art.focalPoint.x * 100)"
                  :aria-valuemin="0"
                  :aria-valuemax="100"
                  @pointerdown="onOverlayPointerDown"
                  @pointermove="onOverlayPointerMove"
                  @pointerup="onOverlayPointerUp"
                  @pointercancel="onOverlayPointerUp"
                  @keydown="onOverlayKeydown"
                >
                  <div class="focal-marker" :style="focalMarkerStyle" />
                </div>
              </div>
              <div class="text-caption text-medium-emphasis mt-1">
                Drag the marker to set the focal point.
              </div>
            </div>
          </v-col>

          <!-- Live previews -->
          <v-col cols="12" md="6">
            <div class="cover-panel">
              <!-- 16:9 group -->
              <div class="text-subtitle-2">
                {{ aspect16Surfaces }} — 16:9 crop
              </div>
              <div class="text-caption text-medium-emphasis mb-2">
                {{ aspect16Hint }}
              </div>
              <div class="preview-group">
                <div class="preview-item">
                  <canvas
                    ref="heroCanvasEl"
                    class="preview-hero"
                    width="600"
                    height="338"
                    aria-label="Hero preview"
                  />
                  <div
                    class="text-caption text-medium-emphasis preview-caption"
                  >
                    Hero
                  </div>
                </div>
                <div class="preview-item">
                  <canvas
                    ref="cardCanvasEl"
                    class="preview-card"
                    width="200"
                    height="112"
                    aria-label="Card preview"
                  />
                  <div
                    class="text-caption text-medium-emphasis preview-caption"
                  >
                    Card
                  </div>
                </div>
              </div>

              <!-- 1:1 share preview mockup -->
              <div class="text-subtitle-2 mt-4">Share preview — 1:1 crop</div>
              <div class="text-caption text-medium-emphasis mb-2">
                {{ aspect11Hint }}
              </div>
              <div class="og-mockup">
                <div class="og-mockup-inner">
                  <canvas
                    ref="ogCanvasEl"
                    class="preview-og"
                    width="460"
                    height="460"
                    aria-label="Share preview"
                  />
                  <div class="og-mockup-avatar" />
                  <div class="og-mockup-title" />
                </div>
              </div>
              <div class="text-caption text-medium-emphasis preview-caption">
                Share preview
              </div>

              <div class="text-caption text-medium-emphasis mt-3">
                Final rendering may differ slightly due to server-side
                processing.
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Mode switch: enter advanced fine-tuning -->
        <div class="mt-3">
          <v-btn
            variant="text"
            size="small"
            prepend-icon="mdi-tune"
            :disabled="props.disabled"
            @click="cropMode = 'advanced'"
          >
            Fine-tune crops
          </v-btn>
        </div>
      </div>

      <!-- Advanced mode: two per-aspect croppers replace focal-point UI -->
      <div v-else-if="cropMode === 'advanced'">
        <div class="d-flex align-center mb-3">
          <v-btn
            variant="text"
            size="small"
            prepend-icon="mdi-arrow-left"
            :disabled="props.disabled"
            @click="cropMode = 'focal'"
          >
            Back to focal point
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="!props.disabled"
            icon="mdi-close"
            size="x-small"
            variant="text"
            color="error"
            aria-label="Remove cover image"
            @click="removeImage"
          />
        </div>

        <ClientOnly>
          <div class="advanced-panel">
            <v-row dense>
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 mb-1">
                  16:9 crop — {{ aspect16Surfaces }}
                </div>
                <VueAdvancedCropper
                  :key="cropperKey16_9"
                  :src="art.originalUrl"
                  :stencil-props="{ aspectRatio: 16 / 9 }"
                  :default-position="defaultPosition16_9"
                  :default-size="defaultSize16_9"
                  class="advanced-cropper"
                  @change="onCropperChange16_9"
                />
                <v-btn
                  v-if="art.overrides['16_9']"
                  variant="text"
                  size="small"
                  class="mt-1"
                  :disabled="props.disabled"
                  @click="resetOverride16_9"
                >
                  Reset to focal-point crop
                </v-btn>
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-subtitle-2 mb-1">1:1 crop — Share preview</div>
                <VueAdvancedCropper
                  :key="cropperKey1_1"
                  :src="art.originalUrl"
                  :stencil-props="{ aspectRatio: 1 }"
                  :default-position="defaultPosition1_1"
                  :default-size="defaultSize1_1"
                  class="advanced-cropper"
                  @change="onCropperChange1_1"
                />
                <v-btn
                  v-if="art.overrides['1_1']"
                  variant="text"
                  size="small"
                  class="mt-1"
                  :disabled="props.disabled"
                  @click="resetOverride1_1"
                >
                  Reset to focal-point crop
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </ClientOnly>
      </div>

      <!-- Upload progress -->
      <v-progress-linear
        v-if="art.runtimeState === 'uploading'"
        :model-value="uploadProgress"
        color="primary"
        class="mt-3"
      />
    </div>

    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      density="compact"
      class="mt-3"
      closable
      @click:close="errorMessage = null"
    >
      {{ errorMessage }}
    </v-alert>
  </div>
</template>

<style scoped>
.cover-editor {
  width: 100%;
}

.cover-panel {
  padding: 8px;
}

.focal-image-wrap {
  position: relative;
  display: inline-block;
  max-width: 100%;
}

.focal-image {
  display: block;
  max-width: 100%;
  height: auto;
  user-select: none;
  -webkit-user-drag: none;
  border-radius: 6px;
}

.focal-overlay {
  position: absolute;
  inset: 0;
  cursor: crosshair;
  touch-action: none;
}

.focal-overlay:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.focal-marker {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.6);
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.preview-group {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.preview-item {
  display: flex;
  flex-direction: column;
}

.preview-caption {
  margin-top: 2px;
  text-align: center;
}

.preview-hero {
  width: 300px;
  height: 169px;
  max-width: 100%;
  background: #f0f0f0;
  border-radius: 4px;
}

.preview-card {
  width: 160px;
  height: 90px;
  background: #f0f0f0;
  border-radius: 4px;
}

.og-mockup {
  background: linear-gradient(135deg, #eef2ff, #ffe4e6);
  border-radius: 8px;
  padding: 12px;
  max-width: 300px;
}

.og-mockup-inner {
  position: relative;
}

.preview-og {
  width: 100%;
  aspect-ratio: 1 / 1;
  height: auto;
  background: #f0f0f0;
  border-radius: 4px;
  display: block;
}

.og-mockup-avatar {
  position: absolute;
  bottom: -18px;
  left: 12px;
  width: 40px;
  height: 40px;
  background: #cbd5e1;
  border: 2px solid #fff;
  border-radius: 50%;
}

.og-mockup-title {
  position: absolute;
  bottom: -10px;
  left: 60px;
  right: 12px;
  height: 8px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 2px;
}

.advanced-panel {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 12px;
}

.advanced-cropper {
  /* Cropper's stencil is position: absolute inside its container; without an
     explicit height, everything collapses to 0. min-width guards against the
     two-column layout squeezing the cropper flat on narrow viewports. */
  height: 400px;
  min-width: 260px;
  background: #f5f5f5;
  border-radius: 4px;
}
</style>
